import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
    ErrorHandler,
    enableProdMode,
    inject,
    provideAppInitializer,
    provideZonelessChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideLoadingBarRouter } from '@ngx-loading-bar/router';
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api';
import { provideOptimus } from '@openng/optimus-ui/config';
import * as Sentry from '@sentry/angular';
import { optimusPreset } from 'assets/primeng/preset';
import { config } from 'devextreme/common';
import { isNil } from 'lodash-es';
import { catchError, of, tap } from 'rxjs';
import { environment } from './environments/environment';

import { AppComponent } from '@app/app.component';
import { authRoutes } from '@app/auth/auth-routes';
import { httpInterceptorProviders } from '@app/shared/http-interceptors';
import type { AppConfig } from '@app/shared/interface/common.interface';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { CommonService } from '@app/shared/services/common.service';
import { GoogleAnalyticsService } from '@app/shared/services/google-analytics.service';
import { ScriptLoaderService } from '@app/shared/services/script-loader.service';
import { StorageService } from '@app/shared/services/storage.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { AppConfigErrorDialogService } from '@app/theme/services/app-config-error-dialog.service';
import { themeRoutes } from '@app/theme/theme-routes';

if (environment.production) {
    enableProdMode();
}

config({
    licenseKey:
        'ewogICJmb3JtYXQiOiAxLAogICJjdXN0b21lcklkIjogIjRiMjkwMTE3LTMzYWItNDBlYi1iNDkxLTM3NTk1MGUyZThlMyIsCiAgIm1heFZlcnNpb25BbGxvd2VkIjogMjUyCn0=.kOst7RkWMXDzZr/yccCwNL8NuKiGmTiQZ6OxhePSICle7k4RGORERDwbpKpDG89W2q4Q3qrLecicax4z92yogGt9mGmoLT6ST3NjPMrCsYxXdn16erYRrXtrDf6wxRidGwaAbw==',
});

bootstrapApplication(AppComponent, {
    providers: [
        provideHttpClient(withInterceptorsFromDi()),
        provideRouter(
            [...authRoutes, ...themeRoutes],
            withRouterConfig({
                paramsInheritanceStrategy: 'always',
            }),
            withComponentInputBinding(),
        ),
        provideLoadingBarRouter(),
        provideAppInitializer(() => {
            return inject(AppConfigService)
                .load()
                .pipe(
                    tap((config: AppConfig) => {
                        const { sentryConfig } = config;
                        // Sometimes we use a hosted dev API - but connect it to local UI for testing.
                        // Ignore the sentry configs from the hosted dev API to avoid sentry noise.
                        if (environment.production && !isNil(sentryConfig)) {
                            Sentry.init({
                                ...sentryConfig,
                                integrations: [Sentry.browserTracingIntegration()],
                            });
                        }
                    }),
                    catchError(() => {
                        // Error is already handled in AppConfigService, don't throw to allow app to bootstrap
                        return of(null);
                    }),
                );
        }),
        provideOptimus({
            theme: {
                preset: optimusPreset,
                options: {
                    prefix: 'p',
                    // We toggle this class at runtime from the theme switcher.
                    darkModeSelector: '.dark',
                    cssLayer: {
                        name: 'optimus',
                        order: 'theme, base, optimus, components, utilities',
                    },
                },
            },
        }),
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        AppConfigService,
        AppConfigErrorDialogService,
        ScriptLoaderService,
        GoogleAnalyticsService,
        StorageService,
        httpInterceptorProviders,
        ToastNotificationService,
        CommonService,
        ConfirmationService,
        MessageService,
        provideZonelessChangeDetection(),
    ],
}).catch(err => console.error(err));
